import { useEffect, useRef, useState } from "react";
import { BackwardButton, ForwardButton } from "./blogCarouselButtons";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Articles } from "../../data/models/article";
import { useNavigate } from "react-router-dom";
import Routes from "router/routes";

gsap.registerPlugin(ScrollTrigger);

interface Props {
  allArticles: Articles[];
  currentArticle: Articles;
}

export const RelatedArticlesCarousel = ({
  allArticles,
  currentArticle,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoScrollInterval = useRef<NodeJS.Timeout>();

  // Refs for animations
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const updatedTextRef = useRef<HTMLParagraphElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const carouselItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Filter articles that share at least one tag with the current article
  const relatedArticles = allArticles.filter((article) => {
    // Skip the current article
    if (article.id === currentArticle.id) return false;

    // Get current article's tag IDs
    const currentTags = currentArticle.tags?.map((tag) => tag.id) || [];
    // Get potential related article's tag IDs
    const articleTags = article.tags?.map((tag) => tag.id) || [];

    // If either article has no tags, don't show as related
    if (currentTags.length === 0 || articleTags.length === 0) return false;

    // Check if they share any tags
    return articleTags.some((tagId) => currentTags.includes(tagId));
  });

  // Auto-scroll functionality for mobile
  useEffect(() => {
    const startAutoScroll = () => {
      autoScrollInterval.current = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex + 1 >= relatedArticles.length ? 0 : prevIndex + 1,
        );
      }, 5000);
    };

    const stopAutoScroll = () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };

    if (window.innerWidth < 1024) {
      startAutoScroll();
    }

    return () => {
      stopAutoScroll();
    };
  }, [relatedArticles.length]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        if (autoScrollInterval.current) {
          clearInterval(autoScrollInterval.current);
        }
      } else {
        if (!autoScrollInterval.current) {
          autoScrollInterval.current = setInterval(() => {
            setCurrentIndex((prevIndex) =>
              prevIndex + 1 >= relatedArticles.length ? 0 : prevIndex + 1,
            );
          }, 5000);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [relatedArticles.length]);

  // Initialize refs array
  useEffect(() => {
    carouselItemsRef.current = carouselItemsRef.current.slice(
      0,
      relatedArticles.length,
    );
  }, [relatedArticles.length]);

  // Animation effects
  useEffect(() => {
    if (!relatedArticles.length) return;

    const ctx = gsap.context(() => {
      // Title and updated text animation
      if (titleRef.current && updatedTextRef.current) {
        gsap.fromTo(
          [titleRef.current, updatedTextRef.current],
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top center+=100",
              toggleActions: "play none none reverse",
            },
          },
        );
      }

      // Navigation buttons animation (desktop)
      if (navigationRef.current?.children) {
        gsap.fromTo(
          navigationRef.current.children,
          {
            opacity: 0,
            x: 20,
            scale: 0.8,
          },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: "back.out(1.7)",
            scrollTrigger: {
              trigger: navigationRef.current,
              start: "top center+=100",
              toggleActions: "play none none reverse",
            },
          },
        );
      }

      // Carousel items animation
      carouselItemsRef.current.forEach((item, index) => {
        if (item) {
          gsap.fromTo(
            item,
            {
              opacity: 0,
              y: 50,
              scale: 0.95,
            },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 1,
              delay: index * 0.2,
              ease: "power2.out",
              scrollTrigger: {
                trigger: carouselRef.current,
                start: "top center+=150",
                toggleActions: "play none none reverse",
              },
            },
          );
        }
      });

      // Pagination dots animation (mobile)
      if (paginationRef.current?.children) {
        gsap.fromTo(
          paginationRef.current.children,
          {
            opacity: 0,
            y: 10,
            scale: 0.5,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.4,
            stagger: 0.05,
            ease: "back.out(2)",
            scrollTrigger: {
              trigger: paginationRef.current,
              start: "top bottom-=50",
              toggleActions: "play none none reverse",
            },
          },
        );
      }
    });

    return () => ctx.revert();
  }, [relatedArticles.length]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + 1 >= relatedArticles.length ? 0 : prevIndex + 1,
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex - 1 < 0 ? relatedArticles.length - 1 : prevIndex - 1,
    );
  };

  const navigate = useNavigate();

  // If no related articles found, return null
  if (relatedArticles.length === 0) return null;

  return (
    <div ref={sectionRef} className="flex flex-col">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-[8px]">
          <p
            ref={titleRef}
            className="font-inter text-[30px] font-medium leading-[125%] tracking-[-0.02em] text-[#131313] lg:text-[42px]"
          >
            Related Articles
          </p>
        </div>
        <div
          ref={navigationRef}
          className="mt-[24px] hidden flex-row gap-5 lg:flex"
        >
          <BackwardButton
            onClick={handlePrev}
            className="bg-[#FFF3ED]"
            iconSrc="/assets/icons/Arrow_Back.svg"
          />
          <ForwardButton onClick={handleNext} className="bg-[#022928]" />
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block">
        <div ref={carouselRef} className="relative mt-[64px] overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 33.33}%)` }}
          >
            {relatedArticles.map((article, index) => (
              <div
                key={article.id}
                ref={(el) => (carouselItemsRef.current[index] = el)}
                onClick={() => {
                  navigate(Routes.BLOG_POST(article.slug), {
                    state: article,
                  });
                }}
                className="hover:scale-104 w-1/3 flex-shrink-0 cursor-pointer px-4"
              >
                <div className="flex flex-col gap-[24px]">
                  <img
                    src={`${process.env.REACT_APP_STRAPI_URL}${article.cover?.url}`}
                    alt={article.title}
                    className="aspect-[4/3] w-full rounded-[20px] object-cover"
                  />
                  <div className="flex flex-col gap-[16px]">
                    <p className="font-inter text-[24px] font-medium leading-[125%] tracking-[-0.01em] text-[#131313]">
                      {article.title}
                    </p>
                    <p className="font-inter text-[14px] font-normal leading-[140%] tracking-[0%] text-[#5A5A59]">
                      {article.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {article.tags?.map((tag) => (
                        <div
                          key={tag.id}
                          className="rounded-full bg-[#FFF3ED] px-3 py-1"
                        >
                          <p className="font-inter text-[12px] font-medium text-[#131313]">
                            {tag.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="block lg:hidden">
        <div className="relative mt-[42px] overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {relatedArticles.map((article, index) => (
              <div
                key={article.id}
                ref={(el) => (carouselItemsRef.current[index] = el)}
                onClick={() => {
                  navigate(Routes.BLOG_POST(article.slug), {
                    state: article,
                  });
                }}
                className="w-full flex-shrink-0 px-4"
              >
                <div className="flex flex-col gap-[24px]">
                  <img
                    src={`${process.env.REACT_APP_STRAPI_URL}${article.cover?.url}`}
                    alt={article.title}
                    className="aspect-[4/3] w-full rounded-[20px] object-cover"
                  />
                  <div className="flex flex-col gap-[16px]">
                    <p className="font-inter text-[24px] font-medium leading-[125%] tracking-[-0.01em] text-[#131313]">
                      {article.title}
                    </p>
                    <p className="font-inter text-[14px] font-normal leading-[140%] tracking-[0%] text-[#5A5A59]">
                      {article.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {article.tags?.map((tag) => (
                        <div
                          key={tag.id}
                          className="rounded-full bg-[#FFF3ED] px-3 py-1"
                        >
                          <p className="font-inter text-[12px] font-medium text-[#131313]">
                            {tag.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          ref={paginationRef}
          className="mb-6 mt-[32px] flex justify-center gap-2"
        >
          {relatedArticles.map((_, index) => (
            <button
              key={index}
              className={`h-2 w-2 rounded-full transition-colors ${
                index === currentIndex ? "bg-[#4FBA6D]" : "bg-[#D4D4D4]"
              }`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
